// src/admin/pages/AdminCoupons.jsx
// Coupon usage overview -- pulls GET /admin/coupons once per page load.
// platform_coupons is a tiny table (you're capping EARLY50 at 100 uses),
// so this is cheap even without any caching.

import { useEffect, useState, useCallback } from "react";
import { Loader2, Tag } from "lucide-react";
import RequireAdmin from "@/admin/components/RequireAdmin";
import AdminLayout from "@/admin/components/AdminLayout";
import adminApi from "@/admin/lib/adminApi";

function CouponCard({ coupon }) {
  const pct =
    coupon.max_redemptions > 0
      ? Math.min(100, Math.round((coupon.times_redeemed / coupon.max_redemptions) * 100))
      : 0;

  return (
    <div className="rounded-3xl border border-ink/10 bg-white p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Tag size={16} className="text-hotpink" />
          <p className="font-display text-xl font-black tracking-tight">{coupon.code}</p>
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
            coupon.active ? "bg-mint/60 text-ink" : "bg-ink/10 text-ink/50"
          }`}
        >
          {coupon.active ? "Active" : "Inactive"}
        </span>
      </div>
      <p className="mt-1 text-sm text-ink/60">{coupon.discount_percent}% off</p>

      <div className="mt-4">
        <div className="flex items-center justify-between text-xs text-ink/50">
          <span>
            {coupon.times_redeemed} / {coupon.max_redemptions} used
          </span>
          <span>{coupon.remaining} left</span>
        </div>
        <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-ink/5">
          <div className="h-full bg-hotpink" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {Object.keys(coupon.status_breakdown || {}).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {Object.entries(coupon.status_breakdown).map(([status, count]) => (
            <span
              key={status}
              className="rounded-full bg-ink/5 px-2 py-0.5 text-xs font-semibold capitalize text-ink/60"
            >
              {status}: {count}
            </span>
          ))}
        </div>
      )}

      <p className="mt-3 text-xs text-ink/40">
        {coupon.expires_at
          ? `Expires ${new Date(coupon.expires_at).toLocaleDateString()}`
          : "No expiry set"}
      </p>
    </div>
  );
}

function AdminCouponsInner() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const { data } = await adminApi.get("/admin/coupons");
    setCoupons(data.coupons);
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    load()
      .catch(() => setError("Couldn't load coupons."))
      .finally(() => setLoading(false));
  }, [load]);

  return (
    <AdminLayout>
      <div className="mx-auto max-w-5xl">
        <h1 className="font-display text-3xl tracking-tighter">Coupons</h1>
        <p className="mt-1 text-sm text-ink/50">
          Platform-level discount codes and their redemption usage.
        </p>

        {loading ? (
          <div className="mt-10 grid place-items-center">
            <Loader2 className="h-5 w-5 animate-spin text-ink/40" />
          </div>
        ) : error ? (
          <p className="mt-10 text-center text-sm text-ink/50">{error}</p>
        ) : coupons.length === 0 ? (
          <p className="mt-10 text-center text-sm text-ink/50">No coupons yet.</p>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {coupons.map((c) => (
              <CouponCard key={c.id} coupon={c} />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default function AdminCoupons() {
  return (
    <RequireAdmin>
      <AdminCouponsInner />
    </RequireAdmin>
  );
}
